import { analytics, isDevAnalyticsHost } from './analytics';
import { hashUserId } from './identity';

export const CONSENT_POLICY = {
  consentVersion: '2026-10-v1',
  noticeVersion: 'unpublished-draft',
  sourceApp: 'assistant',
} as const;
export const CONSENT_LABEL = 'Allow usage analytics to help improve Artstash.';
export const PRIVACY_NOTICE_URL = 'https://www.artstash.io/privacy-policy';
export const CONSENT_COOKIE = 'artstash_pi_consent_dev';
const GATEWAY = 'https://dam-api-dev.artstash.io';
const ISSUER = 'https://auth-dev.artstash.io/realms/artstash-app';

export type ConsentStatus = 'loading' | 'unset' | 'granted' | 'denied' | 'error';
interface Preference {
  status: 'unset' | 'granted' | 'denied';
  decidedAt: string | null;
}

export function isGatewayUserToken(token: string | null, email: string | undefined): boolean {
  if (!token || !email || !isDevAnalyticsHost()) return false;
  try {
    const [header, payload, signature] = token.split('.');
    const decode = (part: string) => JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const claims = decode(payload);
    return Boolean(signature && decode(header).alg === 'RS256' && claims.iss === ISSUER &&
      typeof claims.sub === 'string' && claims.email?.trim().toLowerCase() === email.trim().toLowerCase() &&
      typeof claims.exp === 'number' && claims.exp * 1000 > Date.now());
  } catch {
    return false;
  }
}

export async function consentSubjectTag(email: string): Promise<string> {
  return (await hashUserId(`artstash-consent-v1:${email.trim().toLowerCase()}`)).slice(0, 16);
}

function readCookie(): string {
  return document.cookie.split(';').map(value => value.trim())
    .find(value => value.startsWith(`${CONSENT_COOKIE}=`))?.slice(CONSENT_COOKIE.length + 1) || '';
}

function writeCookie(preference: Preference, subjectTag: string): void {
  if (preference.status === 'unset' || !preference.decidedAt) return;
  const epoch = Math.floor(Date.parse(preference.decidedAt) / 1000);
  const value = `v1.${preference.status === 'granted' ? 'g' : 'd'}.${subjectTag}.${epoch}`;
  if (readCookie() === value) return;
  const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  document.cookie = `${CONSENT_COOKIE}=${value}; Path=/; Max-Age=15552000; SameSite=Lax` +
    (local ? '' : '; Domain=.artstash.io; Secure');
}

function parsePreference(body: unknown): Preference {
  const preference = (body as { productImprovementAnalytics?: Preference })?.productImprovementAnalytics;
  if (!preference || !['unset', 'granted', 'denied'].includes(preference.status) ||
    (preference.status !== 'unset' &&
      (typeof preference.decidedAt !== 'string' || !Number.isFinite(Date.parse(preference.decidedAt))))) {
    throw new Error('Invalid privacy preferences response');
  }
  return preference;
}

/** Only the gateway can grant consent; cookies are invalidation/withdrawal signals. */
export class ConsentSession {
  private active = true;
  private revision = 0;
  private subjectTag = '';
  private lastCookie = '';
  private status: ConsentStatus = 'loading';
  private timer?: ReturnType<typeof setInterval>;
  private channel?: BroadcastChannel;
  private abort = new AbortController();
  private saving = false;
  private fetching = false;
  private refreshPending = false;
  private withdrawalPending = false;

  constructor(
    private token: string,
    private email: string,
    private notify: (status: ConsentStatus) => void,
  ) {}

  async start(): Promise<void> {
    analytics.clearIdentity();
    this.notify('loading');
    try {
      this.subjectTag = await consentSubjectTag(this.email);
      if (!this.active) return;
      if (typeof BroadcastChannel !== 'undefined') {
        this.channel = new BroadcastChannel('artstash-consent');
        this.channel.onmessage = () => this.sync(true);
      }
      window.addEventListener('focus', this.onWake);
      document.addEventListener('visibilitychange', this.onWake);
      this.timer = setInterval(() => this.sync(), 3000);
      await this.refresh();
    } catch (error) {
      this.fail(error);
    }
  }

  private onWake = () => this.sync(true);

  private apply(status: ConsentStatus): void {
    if (!this.active) return;
    this.status = status;
    analytics.setConsent(status === 'granted');
    this.notify(status);
  }

  private fail(error: unknown): void {
    if (!this.active) return;
    console.warn('Privacy preferences unavailable; analytics disabled', error);
    this.apply('error');
  }

  private async request(status?: 'granted' | 'denied'): Promise<Preference> {
    if (!isGatewayUserToken(this.token, this.email)) throw new Error('No valid DEV user token');
    const controller = new AbortController();
    const cancel = () => controller.abort();
    this.abort.signal.addEventListener('abort', cancel);
    const timeout = setTimeout(cancel, 10000);
    try {
      const response = await fetch(`${GATEWAY}/users/me/privacy-preferences` +
        (status ? '/product-improvement-analytics' : ''), {
        method: status ? 'PUT' : 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          ...(status ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(status ? { body: JSON.stringify({ status, ...CONSENT_POLICY }) } : {}),
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit',
      });
      if (!response.ok) throw new Error(`Privacy preferences request failed (${response.status})`);
      const preference = parsePreference(await response.json());
      if (status && preference.status !== status) throw new Error('Privacy decision was not persisted');
      return preference;
    } finally {
      clearTimeout(timeout);
      this.abort.signal.removeEventListener('abort', cancel);
    }
  }

  private async refresh(): Promise<void> {
    if (!this.active) return;
    if (this.saving || this.fetching) {
      this.refreshPending = true;
      return;
    }
    this.refreshPending = false;
    this.fetching = true;
    const revision = ++this.revision;
    try {
      const preference = await this.request();
      if (!this.active || revision !== this.revision) return;
      if (this.withdrawalPending && preference.status === 'granted') {
        // A failed withdrawal must not silently resume collection on the next read.
        this.apply('error');
        return;
      }
      writeCookie(preference, this.subjectTag);
      this.lastCookie = readCookie();
      if (this.status !== preference.status) this.apply(preference.status);
    } catch (error) {
      if (revision === this.revision) this.fail(error);
    } finally {
      this.fetching = false;
      if (this.refreshPending) void this.refresh();
    }
  }

  private sync(force = false): void {
    if (!this.active) return;
    const cookie = readCookie();
    const match = /^v1\.([gd])\.([a-f0-9]{16})\.(\d+)$/.exec(cookie);
    const changed = cookie !== this.lastCookie;
    this.lastCookie = cookie;
    if (match && match[2] === this.subjectTag && changed) {
      // Invalidate in-flight grants before waiting for the authoritative answer.
      this.revision += 1;
      if (match[1] === 'd') this.apply('denied');
      void this.refresh();
    } else if (force || this.status === 'error') {
      void this.refresh();
    }
  }

  async decide(status: 'granted' | 'denied'): Promise<void> {
    if (!this.active || this.saving) return;
    this.saving = true;
    const revision = ++this.revision;
    if (status === 'denied') {
      this.withdrawalPending = true;
      this.apply('denied');
    }
    try {
      const preference = await this.request(status);
      if (!this.active || revision !== this.revision) return;
      writeCookie(preference, this.subjectTag);
      this.lastCookie = readCookie();
      this.withdrawalPending = false;
      this.apply(preference.status);
      this.channel?.postMessage({ changed: true });
    } catch (error) {
      if (revision === this.revision) this.fail(error);
      throw error;
    } finally {
      this.saving = false;
      if (this.refreshPending) void this.refresh();
    }
  }

  stop(): void {
    this.active = false;
    this.revision += 1;
    this.abort.abort();
    clearInterval(this.timer);
    window.removeEventListener('focus', this.onWake);
    document.removeEventListener('visibilitychange', this.onWake);
    this.channel?.close();
    analytics.clearIdentity();
  }
}
